import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CheckCircle2,
  ClipboardList,
  History,
  Pause,
  Pencil,
  Play,
  Settings,
  UploadCloud,
  UserPlus,
  type Users,
} from 'lucide-react';
import { apiRequest, ApiError, apiListAll } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { TextField, TextAreaField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { AddRelationModal } from '../components/shared/AddRelationModal';
import { EntityPicker, type PickedEntity } from '../components/shared/EntityPicker';
import { StatusTransitionButton } from '../components/shared/StatusTransitionButton';
import { SalaryStatusBadge } from '../components/finance/StatusBadge';
import { EditSalaryCycleDrawer } from '../components/finance/EditSalaryCycleDrawer';
import { normaliseSalaryRow } from '../components/finance/normalisers';
import { formatINR, formatDate } from '../lib/format';
import {
  cycleLabel,
  type SalaryCycle,
  type SalaryPaymentJoined,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/salaries/$cycleId')({
  component: SalaryCycleDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes — match the enriched detail GET documented in §4.17.
// The base salary_cycle row has no `status` column today; aggregate state is
// derived from KPIs + the new `isHeld` boolean. Backend agent embeds the
// `createdBy` user, the KPIs, and the hold flag.
// ---------------------------------------------------------------------------

interface CreatedByEmbed {
  id: string;
  displayName: string;
  email: string | null;
}

/** The cycle's summary figures, as GET /api/salaries/cycles/:id sends them. Sums are null for no rows. */
interface CycleKpis {
  records: number;
  netPayablePaise: number | null;
  paidPaise: number | null;
  pendingPaise: number | null;
  heldPaise: number | null;
  paidCount: number;
  heldCount: number;
  /** Pending salaries with an amount — what's left to pay. */
  toPayCount: number;
  teacherCount: number;
  nalimCount: number;
}

interface SalaryCycleDetail extends Omit<SalaryCycle, 'createdBy'> {
  createdBy?: CreatedByEmbed | null;
  kpis?: CycleKpis;
  isHeld?: boolean;
  holdReason?: string | null;
}

interface PayeeSummary {
  id: string;
  name: string;
  role: 'teacher' | 'manager';
  employeeId?: string | null;
}

type Tab = 'payments' | 'missing' | 'settings' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'payments', label: 'Payments', icon: ClipboardList },
  { value: 'missing', label: 'Missing payees', icon: UserPlus },
  { value: 'settings', label: 'Settings & notes', icon: Settings },
  { value: 'activity', label: 'Activity', icon: History },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function SalaryCycleDetailPage(): JSX.Element {
  const { cycleId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role === 'super_admin' || role === 'finance_admin';
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState<Tab>('payments');
  const [editOpen, setEditOpen] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);

  const cycleQuery = useQuery({
    queryKey: ['salary-cycle', 'detail', cycleId] as const,
    queryFn: ({ signal }) =>
      apiRequest<SalaryCycleDetail>(`/api/salaries/cycles/${encodeURIComponent(cycleId)}`, {
        signal,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const cycle = cycleQuery.data;

  const invalidateCycle = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['salary-cycle', 'detail', cycleId] });
    void queryClient.invalidateQueries({ queryKey: ['salaryCycles'] });
  };

  const invalidatePayments = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['salary-cycle', 'payments', cycleId] });
    void queryClient.invalidateQueries({ queryKey: ['salary-cycle', 'missing', cycleId] });
    void queryClient.invalidateQueries({ queryKey: ['salaryPayments'] });
    invalidateCycle();
  };

  const generateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true; created: number }>(
        `/api/salaries/cycles/${encodeURIComponent(cycleId)}/generate-records`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      toast(
        res.created > 0
          ? `Generated ${res.created} record(s).`
          : 'No new payees to add.',
        { variant: 'success' },
      );
      invalidatePayments();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not generate records.', {
        variant: 'error',
      });
    },
  });

  const handleMarkAllPaid = async (): Promise<void> => {
    // Per §4.17 we delegate the bulk transition to a single cycle-scoped
    // endpoint rather than fetch-and-chunk on the client; the backend wraps
    // the audit + payslip side-effects in one batch.
    try {
      const res = await apiRequest<{ ok: true; count: number; skipped: number }>(
        `/api/salaries/cycles/${encodeURIComponent(cycleId)}/mark-all-paid`,
        { method: 'POST' },
      );
      const skipped = res.skipped > 0 ? ` ${res.skipped} with a ₹0 net were left pending.` : '';
      if (res.count > 0) {
        toast(`${res.count} payments marked as paid.${skipped}`, { variant: 'success' });
      } else {
        toast(`No pending payments to mark.${skipped}`, { variant: 'info' });
      }
      invalidatePayments();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not mark all paid.', {
        variant: 'error',
      });
    }
  };

  const handleHold = async (): Promise<void> => {
    try {
      await apiRequest<{ ok: true }>(
        `/api/salaries/cycles/${encodeURIComponent(cycleId)}/hold`,
        { method: 'POST', body: { reason: 'Held by super admin' } },
      );
      toast('Cycle put on hold.', { variant: 'success' });
      invalidateCycle();
      invalidatePayments();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not hold cycle.', {
        variant: 'error',
      });
    }
  };

  const handleRelease = async (): Promise<void> => {
    try {
      await apiRequest<{ ok: true }>(
        `/api/salaries/cycles/${encodeURIComponent(cycleId)}/release`,
        { method: 'POST' },
      );
      toast('Cycle released.', { variant: 'success' });
      invalidateCycle();
      invalidatePayments();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not release cycle.', {
        variant: 'error',
      });
    }
  };

  // A stable object: the edit drawer resets its form whenever this changes.
  const cycleForEdit = useMemo(() => (cycle ? { ...cycle, createdBy: cycle.createdBy?.id ?? null } : null), [cycle]);

  const aggregateLabel = useMemo<{ label: string; tone: 'warning' | 'success' | 'info' }>(() => {
    if (!cycle) return { label: 'Open', tone: 'warning' };
    if (cycle.isHeld) return { label: 'Held', tone: 'info' };
    const k = cycle.kpis;
    if (k && k.paidCount > 0 && k.toPayCount === 0 && k.heldCount === 0) {
      return { label: 'All paid', tone: 'success' };
    }
    return { label: 'Open', tone: 'warning' };
  }, [cycle]);

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!cycle) return [];
    const k = cycle.kpis;
    const net = k?.netPayablePaise ?? 0;
    const paid = k?.paidPaise ?? 0;
    const paidPct = net > 0 ? Math.round((paid / net) * 100) : 0;
    const teacherManager = k ? `${k.teacherCount} / ${k.nalimCount}` : '—';
    return [
      { label: 'Records', value: k ? k.records : '—', onClick: () => setTab('payments') },
      { label: 'Net payable', value: k ? formatINR(net) : '—' },
      {
        label: 'Paid',
        value: k ? formatINR(paid) : '—',
        sub: net > 0 ? `${paidPct}%` : undefined,
      },
      { label: 'Pending', value: k ? formatINR(k.pendingPaise ?? 0) : '—' },
      {
        label: 'Held',
        value: k ? k.heldCount : '—',
      },
      {
        label: 'Teachers / Nalims',
        value: teacherManager,
      },
    ];
  }, [cycle]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={cycleQuery.error}
        title="Salary cycle"
        onRetry={() => cycleQuery.refetch()}
      >
        {cycleQuery.isLoading || !cycle ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={cycleLabel(cycle.year, cycle.month)}
              sublabel={
                <>
                  <span className="tabular-nums">
                    Payout on {formatDate(`${cycle.payoutDate}T00:00:00Z`)}
                  </span>
                  {cycle.createdBy ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>Created by {cycle.createdBy.displayName}</span>
                    </>
                  ) : null}
                </>
              }
              statusChip={<CycleStatusChip label={aggregateLabel.label} tone={aggregateLabel.tone} />}
              backTo="/finance/salaries"
              actions={
                isAdmin ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit cycle
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<UploadCloud size={14} aria-hidden="true" />}
                      loading={generateMutation.isPending}
                      onClick={() => generateMutation.mutate()}
                    >
                      Generate records
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<UserPlus size={14} aria-hidden="true" />}
                      onClick={() => setAddPaymentOpen(true)}
                    >
                      Add ad-hoc payment
                    </Button>
                    <StatusTransitionButton
                      label="Mark all paid"
                      icon={<CheckCircle2 size={14} aria-hidden="true" />}
                      variant="primary"
                      size="sm"
                      disabled={Boolean(cycle.isHeld)}
                      confirmCopy={{
                        title: 'Mark every pending salary as paid?',
                        description:
                          'Every pending salary in this cycle is marked paid by bank transfer with today\'s date, and its payslip is generated. Held payments and ₹0 rows are left alone.',
                        confirmLabel: 'Mark all paid',
                      }}
                      onConfirm={handleMarkAllPaid}
                    />
                    {isSuperAdmin ? (
                      cycle.isHeld ? (
                        <StatusTransitionButton
                          label="Release cycle"
                          icon={<Play size={14} aria-hidden="true" />}
                          variant="secondary"
                          size="sm"
                          confirmCopy={{
                            title: 'Release this salary cycle?',
                            description:
                              'Payouts in this cycle become payable again. Held payment rows stay held until released individually.',
                            confirmLabel: 'Release cycle',
                          }}
                          onConfirm={handleRelease}
                        />
                      ) : (
                        <StatusTransitionButton
                          label="Hold cycle"
                          icon={<Pause size={14} aria-hidden="true" />}
                          variant="danger"
                          size="sm"
                          confirmCopy={{
                            title: 'Hold this salary cycle?',
                            description:
                              'All payouts in this cycle are frozen until the cycle is released.',
                            confirmLabel: 'Hold cycle',
                            variant: 'danger',
                          }}
                          onConfirm={handleHold}
                        />
                      )
                    ) : null}
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Cycle sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'payments' ? (
              <PaymentsTab cycleId={cycle.id} />
            ) : null}
            {tab === 'missing' ? (
              <MissingPayeesTab
                cycleId={cycle.id}
                canManage={isAdmin}
                onAdded={invalidatePayments}
              />
            ) : null}
            {tab === 'settings' ? <SettingsTab cycle={cycle} /> : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="salary_cycle"
                targetId={cycle.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      {isAdmin && cycle ? (
        <EditSalaryCycleDrawer
          cycle={editOpen ? cycleForEdit : null}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            invalidateCycle();
            toast('Cycle saved.', { variant: 'success' });
          }}
        />
      ) : null}

      {isAdmin && cycle ? (
        <AdHocPaymentModal
          open={addPaymentOpen}
          cycleId={cycle.id}
          onClose={() => setAddPaymentOpen(false)}
          onAdded={() => {
            invalidatePayments();
            setAddPaymentOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aggregate status chip — derived from `isHeld` plus KPI completeness because
// salary_cycle has no schema-level status column.
// ---------------------------------------------------------------------------

interface CycleStatusChipProps {
  label: string;
  tone: 'success' | 'warning' | 'info';
}

function CycleStatusChip({ label, tone }: CycleStatusChipProps): JSX.Element {
  const className =
    tone === 'success'
      ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
      : tone === 'info'
        ? 'border-ink-700/30 bg-ink-700/5 text-ink-700 dark:text-parchment-200'
        : 'border-gold-500/40 bg-gold-500/10 text-gold-700 dark:text-gold-100';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        className,
      )}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Payments tab
// ---------------------------------------------------------------------------

function PaymentsTab({ cycleId }: { cycleId: string }): JSX.Element {
  const navigate = useNavigate();
  const paymentsQuery = useQuery({
    queryKey: ['salary-cycle', 'payments', cycleId] as const,
    queryFn: async ({ signal }) => {
      // `/api/salaries/payments` returns either the joined `{ payment, payee }`
      // shape (documented) or a legacy raw row. The normaliser tolerates both
      // so this tab renders across the backend migration.
      // Every page: the API sends at most 100 rows at a time.
      const all = await apiListAll<unknown>('/api/salaries/payments', { signal, query: { cycleId } });
      return all.map(normaliseSalaryRow);
    },
  });

  const items: SalaryPaymentJoined[] = paymentsQuery.data ?? [];
  const errorState = paymentsQuery.isError
    ? {
        message:
          paymentsQuery.error instanceof ApiError
            ? paymentsQuery.error.message
            : 'Could not load payments.',
        onRetry: (): void => {
          void paymentsQuery.refetch();
        },
      }
    : null;

  return (
    <RelationPanel
      title="Payments"
      count={paymentsQuery.data ? items.length : undefined}
      description="Every salary record generated for this cycle."
      loading={paymentsQuery.isLoading && !paymentsQuery.data}
      error={errorState}
      isEmpty={!paymentsQuery.isLoading && items.length === 0}
      empty={{ copy: 'No salary records yet. Generate records to create them.' }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Payee</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2 text-right">Gross</th>
              <th className="px-4 py-2 text-right">Net</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Paid on</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const p = row.payment;
              return (
                <tr
                  key={p.id}
                  onClick={() =>
                    void navigate({
                      to: '/finance/salaries/payments/$paymentId',
                      params: { paymentId: p.id },
                    })
                  }
                  className="cursor-pointer border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
                >
                  <td className="px-4 py-2 align-middle">
                    <div className="font-medium text-[color:var(--color-text)]">
                      {row.payee.name}
                    </div>
                  </td>
                  <td className="px-4 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                    {row.payee.role}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {formatINR(p.grossPaise)}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums font-medium">
                    {formatINR(p.netPaise)}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <SalaryStatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {p.paidAt ? formatDate(p.paidAt, { withTime: true }) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Missing payees tab — admin can add one payee (teacher or manager) at a time
// ---------------------------------------------------------------------------

interface MissingPayeesTabProps {
  cycleId: string;
  canManage: boolean;
  onAdded: () => void;
}

function MissingPayeesTab({
  cycleId,
  canManage,
  onAdded,
}: MissingPayeesTabProps): JSX.Element {
  const { toast } = useToast();
  const [presetPayee, setPresetPayee] = useState<PayeeSummary | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const missingQuery = useQuery({
    queryKey: ['salary-cycle', 'missing', cycleId] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: PayeeSummary[] }>(
        `/api/salaries/cycles/${encodeURIComponent(cycleId)}/missing-payees`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const items = missingQuery.data?.items ?? [];
  const errorState = missingQuery.isError
    ? {
        message:
          missingQuery.error instanceof ApiError
            ? missingQuery.error.message
            : 'Could not load missing payees.',
        onRetry: (): void => {
          void missingQuery.refetch();
        },
      }
    : null;

  const closeModal = (): void => {
    setPresetPayee(null);
    setPickerOpen(false);
  };

  return (
    <>
      <RelationPanel
        title="Missing payees"
        count={missingQuery.data ? items.length : undefined}
        description="Active teachers and nalims who do not yet have a payment row in this cycle."
        action={
          canManage ? (
            <Button
              size="sm"
              icon={<UserPlus size={14} aria-hidden="true" />}
              onClick={() => {
                setPresetPayee(null);
                setPickerOpen(true);
              }}
            >
              Add a payee
            </Button>
          ) : null
        }
        loading={missingQuery.isLoading && !missingQuery.data}
        error={errorState}
        isEmpty={!missingQuery.isLoading && items.length === 0}
        empty={{ copy: 'Every active payee already has a record in this cycle.' }}
      >
        <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
          {items.map((p) => (
            <li
              key={`${p.role}:${p.id}`}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5"
            >
              <div className="min-w-0">
                <div className="font-medium text-[color:var(--color-text)] truncate">
                  {p.name}
                </div>
                <div className="text-xs capitalize text-[color:var(--color-text-muted)]">
                  {p.role}
                  {p.employeeId ? ` · ${p.employeeId}` : ''}
                </div>
              </div>
              {canManage ? (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<UserPlus size={14} aria-hidden="true" />}
                  onClick={() => {
                    setPresetPayee(p);
                  }}
                >
                  Add to cycle
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </RelationPanel>

      {canManage ? (
        <AdHocPaymentModal
          open={pickerOpen || presetPayee !== null}
          cycleId={cycleId}
          presetPayee={presetPayee}
          onClose={closeModal}
          onAdded={() => {
            toast('Payee added to cycle.', { variant: 'success' });
            onAdded();
            closeModal();
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Settings tab — read-only payout date, notes, hold state
// ---------------------------------------------------------------------------

function SettingsTab({ cycle }: { cycle: SalaryCycleDetail }): JSX.Element {
  return (
    <section
      aria-label="Cycle settings"
      className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Settings & notes
      </h3>
      <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SettingRow label="Payout date" value={formatDate(`${cycle.payoutDate}T00:00:00Z`)} />
        <SettingRow label="Year / Month" value={`${cycle.year} · ${String(cycle.month).padStart(2, '0')}`} />
        <SettingRow
          label="Hold state"
          value={cycle.isHeld ? 'Held' : 'Active'}
        />
        <SettingRow
          label="Hold reason"
          value={cycle.holdReason ?? (cycle.isHeld ? '—' : 'Not held')}
        />
      </dl>
      {cycle.notes ? (
        <div className="mt-4">
          <dt className="text-xs text-[color:var(--color-text-muted)]">Notes</dt>
          <dd className="mt-1 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
            {cycle.notes}
          </dd>
        </div>
      ) : (
        <p className="mt-4 text-sm text-[color:var(--color-text-muted)]">
          No notes recorded for this cycle.
        </p>
      )}
    </section>
  );
}

function SettingRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-[color:var(--color-text-muted)]">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-[color:var(--color-text)]">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ad-hoc payment modal — dual-mode teacher/manager picker + amounts
// ---------------------------------------------------------------------------

interface AdHocPaymentModalProps {
  open: boolean;
  cycleId: string;
  presetPayee?: PayeeSummary | null;
  onClose: () => void;
  onAdded: () => void;
}

function AdHocPaymentModal({
  open,
  cycleId,
  presetPayee,
  onClose,
  onAdded,
}: AdHocPaymentModalProps): JSX.Element {
  const [payeeRole, setPayeeRole] = useState<'teacher' | 'manager'>(
    presetPayee?.role ?? 'teacher',
  );
  const [picked, setPicked] = useState<PickedEntity | null>(null);
  const [grossRupees, setGrossRupees] = useState('');
  const [deductionsRupees, setDeductionsRupees] = useState('');
  const [notes, setNotes] = useState('');

  // Sync local state when the modal opens or the preset payee changes.
  // We deliberately reset gross/deductions/notes on every (re)open so a
  // previous entry never bleeds into a fresh session.
  useEffect(() => {
    if (!open) return;
    if (presetPayee) {
      setPayeeRole(presetPayee.role);
      setPicked({ id: presetPayee.id, label: presetPayee.name });
    } else {
      setPicked(null);
    }
    setGrossRupees('');
    setDeductionsRupees('');
    setNotes('');
  }, [presetPayee, open]);

  const targetId = presetPayee?.id ?? picked?.id ?? null;
  const grossPaise = toPaise(grossRupees);
  const deductionsPaise = toPaise(deductionsRupees);

  const canSubmit =
    Boolean(targetId) &&
    Number.isFinite(grossPaise) &&
    grossPaise > 0 &&
    deductionsPaise >= 0 &&
    deductionsPaise <= grossPaise;

  const submit = async (): Promise<{ id: string }> => {
    if (!targetId) {
      throw new ApiError(400, 'invalid', 'Pick a payee to add.');
    }
    if (!Number.isFinite(grossPaise) || grossPaise <= 0) {
      throw new ApiError(400, 'invalid', 'Enter a valid gross amount.');
    }
    const body: Record<string, unknown> = {
      grossPaise,
    };
    if (payeeRole === 'teacher') body.teacherId = targetId;
    else body.managerId = targetId;
    if (deductionsPaise > 0) body.deductionsPaise = deductionsPaise;
    if (notes.trim()) body.notes = notes.trim();
    return apiRequest<{ id: string }>(
      `/api/salaries/cycles/${encodeURIComponent(cycleId)}/payments`,
      { method: 'POST', body },
    );
  };

  const modalTitle = presetPayee
    ? `Add ${presetPayee.name} to cycle`
    : 'Add an ad-hoc payment';

  return (
    <AddRelationModal<{ id: string }>
      open={open}
      onClose={onClose}
      title={modalTitle}
      description="Set the gross amount in rupees; net is gross minus deductions."
      picker={
        presetPayee ? (
          <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-sm">
            <div className="font-medium">{presetPayee.name}</div>
            <div className="text-xs capitalize text-[color:var(--color-text-muted)]">
              {presetPayee.role}
              {presetPayee.employeeId ? ` · ${presetPayee.employeeId}` : ''}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <fieldset className="flex items-center gap-3 text-sm">
              <legend className="sr-only">Payee role</legend>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="payeeRole"
                  value="teacher"
                  checked={payeeRole === 'teacher'}
                  onChange={() => {
                    setPayeeRole('teacher');
                    setPicked(null);
                  }}
                  className="accent-gold-700"
                />
                <span>Teacher</span>
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="payeeRole"
                  value="manager"
                  checked={payeeRole === 'manager'}
                  onChange={() => {
                    setPayeeRole('manager');
                    setPicked(null);
                  }}
                  className="accent-gold-700"
                />
                <span>Nalim</span>
              </label>
            </fieldset>
            <EntityPicker
              entity={payeeRole}
              {...(picked ? { value: picked } : {})}
              onChange={(v) => {
                if (Array.isArray(v)) setPicked(v[0] ?? null);
                else setPicked(v.id ? v : null);
              }}
            />
          </div>
        )
      }
      fields={
        <>
          <TextField
            label="Gross (₹)"
            type="number"
            min={0}
            step="0.01"
            value={grossRupees}
            onChange={(e) => setGrossRupees(e.target.value)}
          />
          <TextField
            label="Deductions (₹)"
            type="number"
            optional
            min={0}
            step="0.01"
            value={deductionsRupees}
            onChange={(e) => setDeductionsRupees(e.target.value)}
          />
          <TextAreaField
            label="Notes"
            optional
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </>
      }
      submitLabel="Add payment"
      canSubmit={canSubmit}
      onSubmit={submit}
      onSuccess={() => {
        onAdded();
      }}
    />
  );
}

// Convert a rupees-string from a number input to paise (integer).
// Returns NaN when the input is blank or unparseable so the canSubmit guard
// can reject it without false positives on `0`.
function toPaise(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return NaN;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100);
}

